import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Metric {
  label: string;
  value: number;
  suffix: string;
  icon: string;
  color: string;
  target: number;
}

function useAnimatedValue(target: number, isVisible: boolean, decimals = 0, duration = 2500) {
  const [val, setVal] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!isVisible || started.current) return;
    started.current = true;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(eased * target);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  return decimals > 0 ? val.toFixed(decimals) : Math.floor(val).toLocaleString();
}

function MetricCard({ metric, isVisible, index }: { metric: Metric; isVisible: boolean; index: number }) {
  const displayValue = useAnimatedValue(metric.target, isVisible, 0);

  const colorClasses: Record<string, { bg: string; text: string; border: string; glow: string }> = {
    primary: { bg: "bg-primary-500/10", text: "text-primary-400", border: "border-primary-400/20", glow: "shadow-primary-500/10" },
    emerald: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-400/20", glow: "shadow-emerald-500/10" },
    accent: { bg: "bg-accent-500/10", text: "text-accent-400", border: "border-accent-400/20", glow: "shadow-accent-500/10" },
    cyan: { bg: "bg-cyan-500/10", text: "text-cyan-400", border: "border-cyan-400/20", glow: "shadow-cyan-500/10" },
  };

  const c = colorClasses[metric.color] || colorClasses.primary;

  return (
    <div
      className={`relative group overflow-hidden rounded-xl border ${c.border} bg-background-950/60 backdrop-blur-sm p-5 md:p-6 transition-all duration-700 hover:border-opacity-40 hover:shadow-lg ${c.glow}`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full ${c.bg} blur-2xl opacity-40 group-hover:opacity-60 transition-opacity`} />

      <div className="relative z-10">
        <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${c.bg} border ${c.border} mb-3`}>
          <i className={`${metric.icon} text-lg ${c.text}`} />
        </div>
        <p className={`font-heading font-bold text-2xl md:text-3xl ${c.text} mb-1`}>
          {displayValue}{metric.suffix}
        </p>
        <p className="text-xs font-label text-foreground-500/60 uppercase tracking-wider">
          {metric.label}
        </p>
      </div>
    </div>
  );
}

export default function DashboardSD() {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const metrics: Metric[] = useMemo(() => [
    { label: t("sd.dashboard.m1.label"), value: 0, suffix: "", icon: "ri-ticket-2-line", color: "primary", target: 3847 },
    { label: t("sd.dashboard.m2.label"), value: 0, suffix: "%", icon: "ri-check-double-line", color: "emerald", target: 99 },
    { label: t("sd.dashboard.m3.label"), value: 0, suffix: "+", icon: "ri-team-line", color: "accent", target: 215 },
    { label: t("sd.dashboard.m4.label"), value: 0, suffix: " min", icon: "ri-timer-line", color: "cyan", target: 12 },
  ], [t]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="dashboard-sd" className="relative py-16 md:py-24 bg-background-950 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(var(--primary-500) / 0.3) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.3) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary-400/20 bg-primary-500/8 mb-4">
            <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
            <span className="text-[10px] font-label font-semibold text-primary-300 uppercase tracking-[0.2em]">
              {t("sd.dashboard.tag")}
            </span>
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-50 mb-3">
            {t("sd.dashboard.title")} <span className="text-primary-400">{t("sd.dashboard.highlight")}</span>
          </h2>
          <p className="text-sm text-foreground-400/60 max-w-lg mx-auto leading-relaxed">
            {t("sd.dashboard.subtitle")}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div
              className={`relative rounded-2xl overflow-hidden border border-primary-400/10 transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Diverse%20professional%20IT%20support%20team%20working%20together%20in%20a%20modern%20bright%20office%2C%20wearing%20headsets%20and%20looking%20at%20monitors%2C%20warm%20genuine%20smiles%2C%20casual%20business%20attire%2C%20natural%20daylight%20through%20large%20windows%2C%20candid%20collaborative%20moment%2C%20editorial%20photography%20style%2C%20soft%20warm%20tones%20with%20subtle%20blue%20screen%20glow%2C%20high%20detail%208K&width=700&height=500&seq=sd-team-metrics&orientation=landscape"
                alt="Service Desk Team"
                width={700}
                height={500}
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/20 border border-primary-400/20">
                    <i className="ri-team-line text-sm text-primary-300" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-sm text-white">{t("sd.dashboard.teamTitle")}</p>
                    <p className="text-[10px] text-white/60 font-label uppercase tracking-wider">{t("sd.dashboard.teamSubtitle")}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {metrics.map((metric, idx) => (
                <MetricCard key={metric.label} metric={metric} isVisible={isVisible} index={idx} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}